import { Component } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  INFORMATION_DETAIL,
  INFORMATION_INSERT,
} from 'manager/http-constants';
import { forkJoin } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import {
  API_RESPONSE,
  INPUT_INFORMATIONS_API_CONSTANT,
} from 'src/app/shared/constant/api-constant';
import { CONSTANT, LOADING_KEY } from 'src/app/shared/constant/constant';
import { MESSAGE_CODE } from 'src/app/shared/constant/message-constant';
import { TOAST } from 'src/app/shared/constant/primeng-constants';
import { GENERATE_INPUT_TYPE } from 'src/app/shared/generate/generate-input/constant';
import {
  LoadData,
  LoadingState,
} from 'src/app/shared/html-parts/loading/loading-state';
import {
  MessageData,
  ToastMessageData,
} from 'src/app/shared/html-parts/message-common/message-data';
import { CommonService } from 'src/app/shared/service/common.service';
import { DbOperationService } from 'src/app/shared/service/db-operation.service';
import { Validator } from 'src/app/shared/validator/validator';
import { PlatformComponent } from '../platform.component';

@Component({
  selector: 'app-g103',
  templateUrl: './g103.component.html',
  styleUrls: ['./g103.component.scss', '../platform.component.scss'],
})

/**
 * お知らせ登録(G103)
 * お知らせ登録確認(G104)
 */
export class G103Component extends PlatformComponent {
  // 画面.入力フォーム
  g103InputForm: FormGroup = this.formBuilder.group({
    // お知らせ区分
    information_kbn: this.formBuilder.control(CONSTANT.EMPTY_STRING, {
      asyncValidators: [
        this.validator.CheckValidator(
          'information',
          'information_kbn',
          INPUT_INFORMATIONS_API_CONSTANT.SINGLE_SELECT_TYPE
        ),
      ],
    }),
    // 見出し
    head: this.formBuilder.control(CONSTANT.EMPTY_STRING, {
      asyncValidators: [
        this.validator.CheckValidator(
          'information',
          'head',
          INPUT_INFORMATIONS_API_CONSTANT.TEXT_TYPE
        ),
      ],
    }),
    // タイトル
    title: this.formBuilder.control(CONSTANT.EMPTY_STRING, {
      asyncValidators: [
        this.validator.CheckValidator(
          'information',
          'title',
          INPUT_INFORMATIONS_API_CONSTANT.TEXT_TYPE
        ),
      ],
    }),
    // 掲載URL
    url: this.formBuilder.control(CONSTANT.EMPTY_STRING, {
      asyncValidators: [
        this.validator.CheckValidator(
          'information',
          'url',
          INPUT_INFORMATIONS_API_CONSTANT.TEXT_TYPE
        ),
      ],
    }),
    // 掲載開始日
    publishe_start: this.formBuilder.control(CONSTANT.EMPTY_STRING, {
      asyncValidators: [
        this.validator.CheckValidator(
          'information',
          'publishe_start',
          INPUT_INFORMATIONS_API_CONSTANT.DATE_TYPE
        ),
      ],
    }),
    // 掲載終了日
    publishe_end: this.formBuilder.control(CONSTANT.EMPTY_STRING, {
      asyncValidators: [
        this.validator.CheckValidator(
          'information',
          'publishe_end',
          INPUT_INFORMATIONS_API_CONSTANT.DATE_TYPE
        ),
      ],
    }),
    // お知らせ内容
    information_detail: this.formBuilder.control(CONSTANT.EMPTY_STRING, {
      asyncValidators: [
        this.validator.CheckValidator(
          'information',
          'information_detail',
          INPUT_INFORMATIONS_API_CONSTANT.TEXTAREA_TYPE
        ),
      ],
    }),
  });

  // お知らせIDを格納
  informationId: string = this.route.snapshot.queryParams.information_id;

  // 確認画面表示フラグ(true=分科会申請登録確認(E205),false=分科会申請登録(E204))
  confirmationFlag: boolean = false;

  constructor(
    public route: ActivatedRoute,
    public commonService: CommonService,
    public dbOperationService: DbOperationService,
    public loadingState: LoadingState,
    private router: Router,
    private formBuilder: FormBuilder,
    private validator: Validator,
    private messageData: MessageData
  ) {
    super(route, commonService, dbOperationService, loadingState);

    // urlをお知らせ登録(G103)に変更する
    this.router.navigate(['pages/platform/g103/' + this.inputType], {
      queryParams: {
        screenId: 'g103',
        information_id: this.informationId,
      },
    });
  }

  ngOnInit(): void {
    // 辞書値リストを取得
    this.getSpecCodeList(1004);

    // 編集初期表示
    this.setEditInitialData();
  }

  /**
   * 編集初期表示
   */
  private setEditInitialData() {
    // 登録タイプ判定
    if (!(GENERATE_INPUT_TYPE.EDIT == this.inputType)) {
      // 編集以外の場合
      return;
    }

    // 画面ロードフラグをON(ロード中状態)
    this.loadingState.loadStart(INFORMATION_DETAIL);

    // お知らせ詳細を取得
    this.dbOperationService
      .getNoTemplateDisplayData(INFORMATION_DETAIL, this.informationId, true)
      .subscribe((response) => {
        // 画面ロードフラグをOFF(ロード終了)
        this.loadingState.loadSleepEnd(0.3, INFORMATION_DETAIL);
        if (this.commonService.checkNoneResponse(response)) {
          return;
        }

        // 初期値セット
        this.commonService.setFormGroupInitialData(
          this.g103InputForm,
          response.body
        );
      });
  }

  /**
   * お知らせ登録(G103)を表示する
   */
  public moveG103(): void {
    // お知らせ登録(G103)へ遷移
    this.router.navigate(['pages/platform/g103/' + this.inputType], {
      queryParams: {
        screenId: 'g103',
        information_id: this.informationId,
      },
    });

    // お知らせ登録(G103)を表示する
    this.confirmationFlag = false;
  }

  /**
   * お知らせ登録確認(G104)を表示する
   */
  public moveG104(): void {
    // 表示する
    this.confirmationFlag = true;

    // 画面カスタムロードフラグをON(ロード中状態)
    this.loadingState.customLoadStart(
      new LoadData({
        loadingText: '',
        background_color: '',
        opacity: 0.3,
      })
    );

    /* 入力項目値バリデーションチェック */
    // 非同期同時実行リスト
    let task: Observable<any>[] = [
      /* 共通バリデーションチェック */
      this.dbOperationService.multiValidationResult(
        'information',
        this.g103InputForm.value
      ),
    ];

    // 非同期同時実行
    forkJoin(task).subscribe((dataList) => {
      // レスポンスを結合
      // const data = this.generateService.JoinValidationResponseList(dataList);

      // 画面ロードフラグをOFF(ロード終了)
      this.loadingState.loadEnd();

      // お知らせ登録確認(G104)へ遷移
      this.router.navigate(
        ['pages/platform/g103/' + this.route.snapshot.params.inputType],
        {
          queryParams: {
            screenId: 'g104',
            information_id: this.informationId,
          },
        }
      );

      // お知らせ登録確認(G104)を表示する
      this.confirmationFlag = true;
    });
  }

  /**
   * お知らせ登録完了(G105)へ遷移する
   */
  public moveG105(): void {
    // お知らせ登録完了(G105)へ遷移
    this.router.navigate(['pages/platform/g105']);

    // 画面ロードフラグをON(ロード中状態)
    this.loadingState.loadStart(LOADING_KEY.DATA_INSERT);

    // 登録タイプ判定
    if (GENERATE_INPUT_TYPE.NEW == this.inputType) {
      // 新規登録の場合

      // お知らせ登録を実施
      this.dbOperationService
        .insertData(INFORMATION_INSERT, this.g103InputForm.value)
        .subscribe((response) => {
          // 画面ロードフラグをOFF(ロード終了)
          this.loadingState.loadSleepEnd(0.3, LOADING_KEY.DATA_INSERT);

          // レスポンス結果を判定
          if (API_RESPONSE.SUCCESS == response.body.Message) {
            // 正常終了の場合

            // お知らせ登録完了(G105)へ遷移
            this.router.navigate(['pages/platform/g105']);
          } else {
            // 異常終了の場合

            // 異常終了メッセージ
            this.messageData.toastMessage(
              new ToastMessageData({
                severity: TOAST.ERROR,
                summary: this.commonService.msg(MESSAGE_CODE.E00001),
                detail: this.commonService.msg(MESSAGE_CODE.E80002),
              })
            );

            // お知らせ登録(G103)を表示する
            this.confirmationFlag = false;
          }
        });
    } else if (GENERATE_INPUT_TYPE.EDIT == this.inputType) {
      // 編集の場合

      // お知らせ更新を実施
      this.dbOperationService
        .updateData(
          INFORMATION_INSERT,
          this.informationId,
          this.g103InputForm.value
        )
        .subscribe((response) => {
          // 画面ロードフラグをOFF(ロード終了)
          this.loadingState.loadSleepEnd(0.3, LOADING_KEY.DATA_INSERT);

          // レスポンス結果を判定
          if (API_RESPONSE.SUCCESS == response.body.Message) {
            // 正常終了の場合

            // お知らせ登録完了(G105)へ遷移
            this.router.navigate(['pages/platform/g105']);
          } else {
            // 異常終了の場合

            // 異常終了メッセージ
            this.messageData.toastMessage(
              new ToastMessageData({
                severity: TOAST.ERROR,
                summary: this.commonService.msg(MESSAGE_CODE.E00001),
                detail: this.commonService.msg(MESSAGE_CODE.E80002),
              })
            );

            // お知らせ登録(G103)を表示する
            this.confirmationFlag = false;
          }
        });
    }
  }
}
